/**
 * Lluvia de semillas y chiles.
 *
 * Siluetas doradas en contorno cayendo muy despacio detras de la pantalla de
 * acceso y de la de carga. Es lo que el negocio vende a granel —chile guajillo,
 * chile ancho, pepitas, granos— convertido en la textura de fondo de la marca.
 *
 * DONDE SI Y DONDE NO
 * Va en el acceso, en la carga y detras del bloque de marca de la barra
 * lateral. Los dos primeros son momentos de espera y el tercero es el rincon
 * del logotipo: en ninguno hay un dato que pueda quedar tapado.
 *
 * NO va detras de la lista de navegacion ni en ninguna pantalla de trabajo, y
 * mucho menos en la caja. El cajero pasa ocho horas frente al mostrador;
 * cualquier cosa moviendose donde tiene que leer deja de ser elegante a la
 * media hora y se vuelve algo que estorba mientras cobra. Por eso la variante
 * `brand` no pasa del 9 % de opacidad: es la unica que vive en pantalla todo el
 * dia y tiene que poder ignorarse.
 *
 * EL CONTORNO ES OBLIGATORIO, NO ESTETICO
 * A 8-16 % de opacidad una silueta rellena se lee como una mancha sin forma. El
 * trazo conserva el perfil, que es lo unico que permite reconocer un chile.
 *
 * QUE HACE QUE UN CHILE PAREZCA UN CHILE
 * El rabo y el gancho de la punta. Sin esos dos rasgos, a este tamaño, cualquier
 * silueta alargada se lee como una gota. Las semillas son al reves: simetricas y
 * sin apendice. Esa es toda la diferencia; el interior no se alcanza a ver.
 *
 * TRES CAPAS DE PROFUNDIDAD
 * Las siluetas no son todas iguales de lejos. Hay tres grupos:
 *
 *   fondo  chicas (0.38-0.56), casi transparentes (5-7.5 %) y lentas (35-46 s)
 *   medio  medianas (0.6-0.86), 8.5-11.5 %, 25-33 s
 *   frente grandes (0.98-1.18), 12.5-14.5 %, 18-23 s
 *
 * Lo que esta cerca pasa mas rapido y se ve mas: es lo mismo que ver caer algo
 * desde una ventana. Las del fondo casi no se distinguen una por una, y ese es
 * el punto: son la textura que llena los huecos entre las de enfrente, que son
 * las unicas que el ojo sigue.
 *
 * LA TABLA ES FIJA, NO ALEATORIA
 * Las posiciones estan escritas a mano y ordenadas por posicion horizontal, con
 * las tres capas entreveradas. Ese orden importa: la variante `soft` toma una de
 * cada dos, y si la tabla estuviera agrupada por capa se llevaria las nueve del
 * fondo y ninguna de enfrente.
 *
 * Las clases y los fotogramas viven en `styles/theme.css`.
 */

import type { CSSProperties, ReactNode } from 'react';
import { cx } from './ui.js';

interface Seed {
  /** Posicion horizontal, en porcentaje del contenedor. */
  x: number;
  /** Factor sobre 30 px de lado. */
  scale: number;
  /** Segundos que tarda en cruzar de arriba abajo. */
  duration: number;
  opacity: number;
  /** Segundos del vaiven lateral. */
  sway: number;
  /** Cuanto se desplaza de lado, en pixeles. */
  drift: number;
  shape: ShapeKey;
}

type ShapeKey = 'guajillo' | 'ancho' | 'pepita' | 'grano';

const SHAPES: Record<ShapeKey, ReactNode> = {
  // Cuerpo largo y ganchudo, caliz y rabo.
  guajillo: (
    <>
      <path d="M11 7.6C16.8 10 16 18.6 7.6 22.2C8.6 18.6 9.6 13.2 11 7.6Z" />
      <path d="M8.9 8.6C9.9 6.7 12.4 6.3 14 7.6" />
      <path d="M11.4 6.8C10.7 4.7 11.4 3 13.4 2.2" />
    </>
  ),
  // Mas ancho y con el pliegue que le deja el secado.
  ancho: (
    <>
      <path d="M10.6 8C18 9.6 19 17.4 11.6 22C7.6 19.6 7.2 13 10.6 8Z" />
      <path d="M12.4 10.6C13.8 14.1 13.6 18.2 12 21.1" />
      <path d="M8.4 9C9.4 7 12.2 6.6 14 8" />
      <path d="M11 7.2C10.4 5.2 11 3.4 13 2.6" />
    </>
  ),
  // Gota puntiaguda con reborde: la pepita de calabaza.
  pepita: (
    <>
      <path d="M12 3.4C15.9 7 17.5 10.7 17.5 14.3C17.5 18.3 15.1 21 12 21C8.9 21 6.5 18.3 6.5 14.3C6.5 10.7 8.1 7 12 3.4Z" />
      <path d="M12 6.4C14.7 9.2 15.8 11.9 15.8 14.4C15.8 17.1 14.2 18.9 12 18.9C9.8 18.9 8.2 17.1 8.2 14.4C8.2 11.9 9.3 9.2 12 6.4Z" />
    </>
  ),
  // Redondeado por los dos extremos, para que no se confunda con la pepita.
  grano: (
    <>
      <ellipse cx="12" cy="12.5" rx="5.2" ry="7.6" transform="rotate(-22 12 12.5)" />
      <path d="M9.2 7.4C11.2 10.4 11.4 14.6 9.8 18" />
    </>
  ),
};

/**
 * Ordenadas por posicion horizontal, con las tres capas entreveradas. Los
 * huecos entre columnas son irregulares a proposito: repartirlas cada 10 % deja
 * ver la reticula, y en cuanto se ve la reticula deja de parecer que cae algo.
 */
const SEEDS: readonly Seed[] = [
  { x: 3, scale: 0.42, duration: 44, opacity: 0.055, sway: 17, drift: 6, shape: 'pepita' },
  { x: 6, scale: 1.05, duration: 21, opacity: 0.13, sway: 7, drift: 16, shape: 'guajillo' },
  { x: 9, scale: 0.72, duration: 30, opacity: 0.095, sway: 11, drift: 11, shape: 'ancho' },
  { x: 14, scale: 0.5, duration: 38, opacity: 0.065, sway: 15, drift: 7, shape: 'guajillo' },
  { x: 19, scale: 0.64, duration: 33, opacity: 0.085, sway: 12, drift: 10, shape: 'grano' },
  { x: 26, scale: 0.38, duration: 46, opacity: 0.05, sway: 18, drift: 5, shape: 'grano' },
  { x: 30, scale: 0.8, duration: 27, opacity: 0.105, sway: 9, drift: 13, shape: 'guajillo' },
  { x: 35, scale: 0.54, duration: 36, opacity: 0.07, sway: 14, drift: 8, shape: 'ancho' },
  { x: 38, scale: 1.18, duration: 18, opacity: 0.145, sway: 6, drift: 18, shape: 'ancho' },
  { x: 44, scale: 0.68, duration: 31, opacity: 0.09, sway: 11, drift: 11, shape: 'pepita' },
  { x: 47, scale: 0.44, duration: 42, opacity: 0.06, sway: 16, drift: 6, shape: 'pepita' },
  { x: 51, scale: 0.86, duration: 25, opacity: 0.115, sway: 8, drift: 14, shape: 'guajillo' },
  { x: 56, scale: 0.52, duration: 37, opacity: 0.068, sway: 13, drift: 9, shape: 'guajillo' },
  { x: 59, scale: 0.98, duration: 23, opacity: 0.125, sway: 8, drift: 15, shape: 'ancho' },
  { x: 65, scale: 0.6, duration: 32, opacity: 0.088, sway: 12, drift: 9, shape: 'grano' },
  { x: 68, scale: 0.4, duration: 45, opacity: 0.052, sway: 17, drift: 5, shape: 'grano' },
  { x: 76, scale: 0.78, duration: 28, opacity: 0.1, sway: 10, drift: 12, shape: 'ancho' },
  { x: 81, scale: 0.56, duration: 35, opacity: 0.075, sway: 14, drift: 8, shape: 'ancho' },
  { x: 85, scale: 1.1, duration: 20, opacity: 0.14, sway: 7, drift: 17, shape: 'guajillo' },
  { x: 88, scale: 0.66, duration: 30, opacity: 0.092, sway: 11, drift: 10, shape: 'pepita' },
  { x: 93, scale: 0.46, duration: 40, opacity: 0.062, sway: 16, drift: 7, shape: 'pepita' },
];

/**
 * Razon aurea. Es lo que reparte las fases de caida.
 *
 * EL PROBLEMA QUE RESUELVE. Cada silueta empieza su ciclo con un retraso
 * negativo, o sea ya empezada, para que al abrir la pantalla la lluvia lleve
 * rato cayendo en vez de arrancar como una cortina que baja de golpe. Pero si
 * esos retrasos se eligen a ojo, dos siluetas coinciden y otras dos dejan un
 * hueco largo donde no pasa nada: la lluvia se ve a tirones.
 *
 * La parte fraccionaria de los multiplos de un numero irracional reparte puntos
 * en un intervalo de la forma mas pareja posible sin llegar a ser regular, y la
 * razon aurea es el irracional que mejor lo hace —es el mas dificil de
 * aproximar con fracciones, que es justo lo que hace que la sucesion nunca se
 * cierre sobre si misma—. Con `Math.random()` saldrian racimos y huecos: el azar
 * se amontona, y ademas el fondo cambiaria en cada re-dibujo de React.
 */
const GOLDEN = 0.618033988749895;

/** Fase de la silueta `index` dentro de su ciclo, entre 0 y 1. */
function phase(index: number, offset: number): number {
  return (index * GOLDEN + offset) % 1;
}

/**
 * Siluetas del bloque de marca de la barra lateral.
 *
 * Es una tira de unos 240 x 72 px, no una pantalla: las de arriba, de hasta
 * 35 px, no cabrian —una sola taparia media franja— y por eso esta variante
 * lleva su propio lado base de 13 px y su propia tabla.
 *
 * Son siete y ninguna pasa del 9 %. Esta es la unica lluvia que el cajero tiene
 * enfrente las ocho horas del turno, asi que el listón no es "que se vea bien"
 * sino "que se pueda ignorar sin esfuerzo".
 *
 * Los ciclos son largos para el recorrido que tienen —88 px en 19 a 37 s, entre
 * 2 y 5 px por segundo—: a esa velocidad el movimiento se percibe de reojo pero
 * no jala la vista cuando uno esta leyendo el menu de al lado.
 */
const BRAND_SEEDS: readonly Seed[] = [
  { x: 6, scale: 0.7, duration: 34, opacity: 0.055, sway: 11, drift: 4, shape: 'pepita' },
  { x: 21, scale: 1, duration: 22, opacity: 0.085, sway: 7, drift: 6, shape: 'guajillo' },
  { x: 34, scale: 0.62, duration: 37, opacity: 0.045, sway: 13, drift: 3, shape: 'grano' },
  { x: 49, scale: 1.15, duration: 19, opacity: 0.09, sway: 6, drift: 7, shape: 'ancho' },
  { x: 63, scale: 0.78, duration: 31, opacity: 0.06, sway: 9, drift: 5, shape: 'guajillo' },
  { x: 78, scale: 0.9, duration: 24, opacity: 0.075, sway: 8, drift: 6, shape: 'ancho' },
  { x: 91, scale: 0.68, duration: 28, opacity: 0.05, sway: 12, drift: 4, shape: 'pepita' },
];

/**
 * Cada sitio donde cae la lluvia, con su tabla y el lado base de sus siluetas.
 *
 * `soft` toma una de cada dos de la tabla grande. Funciona porque `SEEDS` esta
 * ordenada por posicion horizontal con las tres capas entreveradas: saltando de
 * dos en dos salen once repartidas a lo ancho y con mezcla de profundidades.
 */
const VARIANTS = {
  /** Acceso: la pantalla completa. */
  full: { seeds: SEEDS, size: 30 },
  /** Pantalla de carga: dura poco, no alcanza a distinguirse el doble. */
  soft: { seeds: SEEDS.filter((_, index) => index % 2 === 0), size: 30 },
  /** Bloque de marca de la barra lateral. */
  brand: { seeds: BRAND_SEEDS, size: 13 },
} satisfies Record<string, { seeds: readonly Seed[]; size: number }>;

/**
 * Giro inicial y final del vaiven, derivados del indice.
 *
 * Se calculan en lugar de escribirse en la tabla porque son veintiun pares de
 * numeros que a nadie le importan: lo unico que hace falta es que no se
 * repitan. Los multiplicadores son primos para que el ciclo no se cierre antes
 * de acabar la lista y dos siluetas seguidas no giren igual.
 */
function turn(index: number): { from: string; to: string } {
  return {
    from: `${((index * 37) % 70) - 35}deg`,
    to: `${((index * 53) % 70) - 35}deg`,
  };
}

export function SeedRain({
  className,
  variant = 'full',
}: {
  className?: string;
  variant?: keyof typeof VARIANTS;
}): ReactNode {
  const { seeds, size } = VARIANTS[variant];

  return (
    <div
      aria-hidden
      className={cx('seed-rain', className)}
      style={{ '--seed-size': `${size}px` } as CSSProperties}
    >
      {seeds.map((seed, index) => {
        const rotation = turn(index);

        return (
          <span
            key={`${seed.x}-${seed.shape}`}
            className="seed-fall"
            style={
              {
                '--seed-x': `${seed.x}%`,
                '--seed-duration': `${seed.duration}s`,
                '--seed-delay': `${(-seed.duration * phase(index, 0)).toFixed(2)}s`,
                '--seed-opacity': seed.opacity,
              } as CSSProperties
            }
          >
            <svg
              viewBox="0 0 24 24"
              className="seed-shape"
              style={
                {
                  '--seed-scale': seed.scale,
                  '--seed-sway': `${seed.sway}s`,
                  // El vaiven lleva su propia fase, desplazada respecto de la de
                  // caida. Con la misma, la silueta llegaria siempre al mismo
                  // alto en el mismo punto del bamboleo y se notaria el patron.
                  '--seed-sway-delay': `${(-seed.sway * phase(index, 0.37)).toFixed(2)}s`,
                  '--seed-drift': `${seed.drift}px`,
                  '--seed-turn-from': rotation.from,
                  '--seed-turn-to': rotation.to,
                } as CSSProperties
              }
            >
              {SHAPES[seed.shape]}
            </svg>
          </span>
        );
      })}
    </div>
  );
}
